import React, { Fragment } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  addIndex,
  ascend,
  findIndex,
  ifElse,
  identity,
  lensProp,
  map,
  mergeRight,
  pipe,
  prop,
  propEq,
  set,
  sort,
  filter,
  isEmpty,
} from "ramda";
import clsx from "clsx";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove as reorderArray,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { useSelector, useDispatch } from "react-redux";
import {
  getRemoveTrackDialogVisible,
  getSubmissionFiles,
} from "../../common/selectors";
import { changeSubmissionFiles } from "../../submission/submissionActions";

import ClearIcon from "@material-ui/icons/Clear";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import ReorderIcon from "@material-ui/icons/Reorder";

import IntlTextField from "../../general-components/IntlTextField";
import SortableItem from "../../general-components/SortableItem";
import { SHOW_REMOVE_TRACK_DIALOG } from "../fileDetailsActions";
import RemoveTrackDialog from "./RemoveTrackDialog";

const useStyles = makeStyles(theme => ({
  handle: {
    cursor: "grab",
    color: theme.palette.primary.main,
  },

  handleDragging: {
    cursor: "grabbing",
  },

  inputUnderline: {
    "&::before, &:hover:not(.Mui-disabled)::before": {
      borderBottomStyle: "none",
    },
  },
}));

const Track = React.memo(function Track(props) {
  const {
    track,
    isDragging,
    attributes,
    listeners,
    handleTitleChange,
    handleTrackRemove,
  } = props;
  const classes = useStyles();

  return (
    <Grid container item alignItems="center">
      <IconButton
        className={clsx(classes.handle, {
          [classes.handleDragging]: isDragging,
        })}
        aria-label={`Reorder track: ${track.title}`}
        {...listeners}
        {...attributes}
      >
        <ReorderIcon />
      </IconButton>

      <Grid item xs>
        <IntlTextField
          id={track.title}
          inputProps={{
            "aria-label": `Edit title for track ${track.title}`,
          }}
          fullWidth
          value={track.title}
          onChange={handleTitleChange(track.fileId)}
          InputProps={{
            classes: {
              underline: clsx({ [classes.inputUnderline]: isDragging }),
            },
            endAdornment: (
              <IconButton
                aria-label={`remove track with title ${track.title}`}
                onClick={handleTrackRemove(track)}
              >
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      </Grid>
    </Grid>
  );
});

const mapIndexed = addIndex(map);
export const mapOrders = mapIndexed((file, index) =>
  mergeRight(file, { order: index })
);

const setProp = (prop, val) => set(lensProp(prop), val);
const updateTrack = (id, prop, val) =>
  map(ifElse(propEq("fileId", id), setProp(prop, val), identity));

const findTrack = (id, files) => findIndex(propEq("fileId", id), files);
const reorderTracks =
  ({ active, over }) =>
  files => {
    const oldIndex = findTrack(active.id, files);
    const newIndex = findTrack(over.id, files);
    return mapOrders(reorderArray(files, oldIndex, newIndex));
  };

const EditAlbumTracks = React.memo(function EditAlbumTracks(props) {
  const dispatch = useDispatch();
  const setSubmissionFiles = pipe(changeSubmissionFiles, dispatch);
  const tracks = useSelector(getSubmissionFiles);
  const confirmVisible = useSelector(getRemoveTrackDialogVisible);

  //-- Manage Drag and Drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSubmissionFiles(reorderTracks({ active, over })(tracks));
    }
  };

  const handleTitleChange = id => event => {
    const title = event.target.value;
    setSubmissionFiles(updateTrack(id, "title", title)(tracks));
  };

  const onRemoveTrack = track => () =>
    dispatch({ type: SHOW_REMOVE_TRACK_DIALOG, track });

  return (
    <Fragment>
      <DndContext
        onDragEnd={handleDragEnd}
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={!isEmpty(tracks) ? tracks.map(track => track.fileId) : []}
          strategy={verticalListSortingStrategy}
        >
          <Grid container>
            {pipe(
              filter(propEq("fileType", "SUBMISSION")),
              sort(ascend(prop("order"))),
              map(track => (
                <SortableItem
                  key={track.fileId}
                  id={track.fileId}
                  renderChild={({ ...props }) => (
                    <Track
                      {...props}
                      track={track}
                      handleTitleChange={handleTitleChange}
                      handleTrackRemove={onRemoveTrack}
                    />
                  )}
                />
              ))
            )(tracks)}
          </Grid>
        </SortableContext>
      </DndContext>
      {confirmVisible && <RemoveTrackDialog />}
    </Fragment>
  );
});

export default EditAlbumTracks;
